import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";

import { CustomTable } from "../../../components/DataTable";
import { CustomActionMenu } from "../../../components/Menu";
import { ReactComponent as EyeIcon } from "../../../assets/svg/eye.svg";
import { ReactComponent as WarningIcon } from "../../../assets/svg/WarningRed.svg";
import { colors } from "../../../theme/colors";
import type { SpecimenAuditEvent } from "../../../services/api";
import { severityBorderColor } from "../utils";
import { StatusTransition, SeverityPill } from "./StatusPill";

type Props = {
  events: SpecimenAuditEvent[];
  isLoading: boolean;
  isError: boolean;
  onRowClick: (event: SpecimenAuditEvent) => void;
};

const cellText = {
  fontSize: 15,
  color: colors.lighterBlack,
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: "2",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
} as const;

const ActivityFeed = ({ events, isLoading, isError, onRowClick }: Props) => {
  if (isError) {
    return (
      <Typography sx={{ color: colors.red, mt: 2 }}>
        Failed to load activity feed.
      </Typography>
    );
  }

  // The feed is a scanning surface: when did the status move, on which
  // specimen, and does it need attention. Actor, bag QR and request form are
  // deliberately not columns here — they are per-event detail, shown on the
  // timeline screen this feed links into (and in the CSV export), which keeps
  // the row narrow enough to read without horizontal scrolling.
  const fields = [
    {
      id: "occurred_at",
      label: "Time",
      flex: 1.6,
      sortable: true,
      render: (event: SpecimenAuditEvent) => (
        <Typography sx={cellText}>
          {event.occurred_at
            ? dayjs(event.occurred_at).format("DD/MM/YYYY hh:mm A")
            : "—"}
        </Typography>
      ),
    },
    {
      id: "specimen_qr",
      label: "Specimen QR",
      flex: 1.5,
      render: (event: SpecimenAuditEvent) => (
        <Typography sx={cellText}>{event.specimen_qr || "—"}</Typography>
      ),
    },
    {
      id: "transition",
      label: "Transition",
      // CustomTable centres a column's header and cells only for "badge";
      // the chips are centred, so the header follows.
      type: "badge",
      // The chips stack instead of sitting side by side, so this needs far less
      // width than it did — that slack pays for the split Bag / Request form.
      flex: 1.8,
      render: (event: SpecimenAuditEvent) => {
        const from = event.status_transition_from;
        const to = event.status_transition_to;

        if (!from && !to) return <Typography sx={cellText}>—</Typography>;

        // Status labels run to 22 characters ("IN OT DROP OFF PENDING"), so two
        // pills side by side always truncate. Read the step top-to-bottom
        // instead — origin chip, connector, destination chip — which gives each
        // chip the full column width.
        return <StatusTransition from={from} to={to} layout="column" />;
      },
    },
    {
      // Two events on the same specimen in the same minute (e.g. CREATED then
      // PRINTED) are otherwise indistinguishable in the feed — both show the
      // same time and the same unchanged status, and read as duplicates.
      id: "action",
      label: "Action",
      flex: 1,
      render: (event: SpecimenAuditEvent) => (
        <Typography sx={cellText}>{event.action || "—"}</Typography>
      ),
    },
    {
      id: "is_discrepancy",
      label: "Discrepancy",
      type: "badge",
      flex: 1.2,
      render: (event: SpecimenAuditEvent) =>
        event.is_discrepancy ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* Same red warning mark the specimen listing flags a
                discrepancy with, so the two screens read alike. */}
            <WarningIcon aria-hidden width={16} height={16} />
            <Typography sx={{ ...cellText, fontSize: 13 }}>Yes</Typography>
          </Box>
        ) : (
          <Typography sx={cellText}>—</Typography>
        ),
    },
    {
      id: "severity",
      label: "Severity",
      type: "badge",
      flex: 1,
      render: (event: SpecimenAuditEvent) => (
        <SeverityPill severity={event.severity} />
      ),
    },
    {
      id: "actions",
      label: "",
      type: "action",
      flex: 0.5,
      render: (event: SpecimenAuditEvent) => (
        <CustomActionMenu
          item={event}
          isActive={false}
          handleChange={() => onRowClick(event)}
          actions={[
            {
              label: "View timeline",
              value: "view",
              icon: <EyeIcon height={24} width={24} />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 500, mb: 1 }}>
        Activity feed
      </Typography>
      <CustomTable<SpecimenAuditEvent>
        fields={fields}
        data={events}
        loading={isLoading}
        // The transition cell stacks "from" above the destination pill, which
        // does not fit the default 51px row.
        isLargeRow
        // Severity comes straight from the API — an amber rail for a warning,
        // red for an SLA breach — so a row that needs attention is visible
        // without reading the severity column.
        rowTestId={(event: SpecimenAuditEvent) => `activity-row-${event.id}`}
        rowSx={(event: SpecimenAuditEvent) => {
          const borderColor = severityBorderColor(event.severity);
          return borderColor === "transparent"
            ? {}
            : { borderLeft: `4px solid ${borderColor}` };
        }}
        // Floor that keeps every column at its content width; below this the
        // feed scrolls sideways rather than truncating.
        minTableWidth={1000}
        paginationRequired
        defaultOrderBy="occurred_at"
        defaultOrder="Dsc"
        onDoubleClick={(event: SpecimenAuditEvent) => onRowClick(event)}
      />
    </Box>
  );
};

export default ActivityFeed;
